/**
 * session-pagination.js
 *
 * Module de pagination client-side pour la liste des sessions
 * dans les modales d'inscription GoBooking (étape 1 « Session »).
 *
 * Usage (ES module) :
 *   import { initSessionPagination } from './session-pagination.js';
 *
 * Usage (global, pour les scripts inline non-bundlés) :
 *   window.goBooking.initSessionPagination(...)
 */

const SESSIONS_PER_PAGE = 10;

/**
 * Échappe les caractères HTML pour éviter les injections XSS.
 * @param {*} str
 * @returns {string}
 */
function esc(str) {
    if (str === null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Crée et retourne un élément <button> représentant une session.
 *
 * @param {Object} session              Objet session provenant de l'API AJAX
 * @param {number} defaultMaxInscr      Valeur par défaut pour max_inscriptions_user
 * @returns {HTMLButtonElement}
 */
function renderSessionItem(session, defaultMaxInscr = 2) {
    const btn = document.createElement('button');
    btn.type      = 'button';
    btn.className = 'list-group-item list-group-item-action';

    const horaire          = session.horaire || `${session.heure_debut || ''} - ${session.heure_fin || ''}`;
    const formattedHoraire = horaire.replace(/\b(\d{2}:\d{2}):\d{2}\b/g, '$1');
    const places           = parseInt(session.places_disponibles, 10);

    // Attributs data (conservés pour compatibilité avec les handlers existants)
    btn.dataset.sessionId           = session.id;
    btn.dataset.sessionDate         = session.formatted_date || '';
    btn.dataset.sessionDay          = session.day_name || '';
    btn.dataset.sessionHoraire      = horaire;
    btn.dataset.sessionLieu         = session.lieu_depart || 'Lieu non spécifié';
    btn.dataset.sessionVille        = session.ville || '';
    btn.dataset.sessionPlaces       = session.places_disponibles;
    btn.dataset.maxInscriptions     = session.max_inscriptions_user || defaultMaxInscr;
    btn.dataset.activiteTitre       = session.activite_titre || '';
    btn.dataset.activiteDescription = session.activite_description || '';

    // Badge places
    const badgeOk   = places === -1 || places > 0;
    const badgeCls  = badgeOk ? 'bg-success' : 'bg-danger';
    const badgeIcon = badgeOk ? 'bi-person-check' : 'bi-person-x';
    let   placesText;
    if (places === -1)     placesText = 'Places illimitées';
    else if (places === 0) placesText = 'Complet';
    else                   placesText = `${places} place${places !== 1 ? 's' : ''} sur ${parseInt(session.places_totales, 10) || '?'}`;

    // Bloc activité (optionnel)
    const activiteHtml = session.has_activite && session.activite_titre
        ? `<div class="small text-info mt-1"><i class="bi bi-info-circle"></i> Activité : ${esc(session.activite_titre)}</div>`
        : '';

    // Lieu + ville
    const lieuVille = esc(session.lieu_depart || 'Lieu non spécifié')
        + (session.ville ? `, ${esc(session.ville)}` : '');

    btn.innerHTML = `
        <div class="d-flex justify-content-between align-items-center">
            <div>
                <strong>${esc(session.formatted_date || '')} (${esc(session.day_name || '')})</strong>
                <div>${esc(formattedHoraire)}</div>
                <div class="small text-muted">${lieuVille}</div>
                ${activiteHtml}
            </div>
            <span class="badge ${badgeCls} d-flex align-items-center">
                <i class="bi ${badgeIcon} me-1"></i>
                ${esc(placesText)}
            </span>
        </div>`.trim();

    return btn;
}

/**
 * Retourne le HTML des contrôles de pagination à insérer après `.sessions-list`.
 * Utilisé par les templates qui construisent leur modal via une chaîne HTML.
 *
 * @returns {string}
 */
function paginationControlsHTML() {
    return `
        <div class="sessions-pagination mt-3 d-flex justify-content-between align-items-center">
            <small class="text-muted sessions-page-info"></small>
            <nav aria-label="Navigation des sessions">
                <ul class="pagination pagination-sm mb-0">
                    <li class="page-item sessions-prev">
                        <button class="page-link" type="button">&#8249; Précédent</button>
                    </li>
                    <li class="page-item sessions-next">
                        <button class="page-link" type="button">Suivant &#8250;</button>
                    </li>
                </ul>
            </nav>
        </div>`;
}

/**
 * Initialise la pagination client-side des sessions dans la `.sessions-list`
 * d'une modale d'inscription.
 *
 * La liste `.sessions-list` doit être vide à l'appel (aucun item pré-rendu).
 * Les contrôles `.sessions-pagination`, `.sessions-prev`, `.sessions-next` et
 * `.sessions-page-info` doivent exister dans le DOM de la modale.
 *
 * @param {HTMLElement} modal           Élément modal DOM
 * @param {Array}       allSessions     Toutes les sessions disponibles
 * @param {Function}    onSelect        Callback(session|null, element|null) à la sélection
 *                                      Appelé avec (null, null) lors d'une désélection (ex: changement de filtre)
 * @param {number}      [perPage=5]     Sessions affichées par page
 * @param {number}      [defaultMaxInscr=2]  Fallback pour max_inscriptions_user
 *
 * @returns {{
 *   setFilter    : (activity: string) => void,
 *   selectById   : (id: string|number) => boolean,
 *   autoSelectIfSingle : () => boolean,
 *   getSelectedId: () => string|null
 * }}
 */
function initSessionPagination(modal, allSessions, onSelect, perPage, defaultMaxInscr) {
    if (perPage === undefined || perPage === null) perPage = SESSIONS_PER_PAGE;
    if (defaultMaxInscr === undefined || defaultMaxInscr === null) defaultMaxInscr = 2;

    let filtered    = allSessions.slice();
    let currentPage = 1;
    let selectedId  = null;

    const list       = modal.querySelector('.sessions-list');
    const prevItem   = modal.querySelector('.sessions-prev');
    const nextItem   = modal.querySelector('.sessions-next');
    const pageInfo   = modal.querySelector('.sessions-page-info');
    const pagination = modal.querySelector('.sessions-pagination');

    function totalPages() {
        return Math.max(1, Math.ceil(filtered.length / perPage));
    }

    function render() {
        if (!list) return;
        list.innerHTML = '';

        const tp    = totalPages();
        const start = (currentPage - 1) * perPage;
        const end   = Math.min(start + perPage, filtered.length);
        const page  = filtered.slice(start, end);

        page.forEach(function(session) {
            const item = renderSessionItem(session, defaultMaxInscr);
            if (String(session.id) === selectedId) {
                item.classList.add('selected');
            }
            item.addEventListener('click', function() {
                list.querySelectorAll('.list-group-item').forEach(function(el) {
                    el.classList.remove('selected');
                });
                item.classList.add('selected');
                selectedId = String(session.id);
                if (onSelect) onSelect(session, item);
            });
            list.appendChild(item);
        });

        // Texte d'information (ex: "1–5 sur 12 sessions")
        if (pageInfo) {
            if (filtered.length === 0) {
                pageInfo.textContent = 'Aucune session disponible';
            } else {
                pageInfo.textContent = (start + 1) + '\u2013' + end + ' sur ' + filtered.length
                    + ' session' + (filtered.length > 1 ? 's' : '');
            }
        }

        // Masquer les contrôles si tout tient sur une page
        if (pagination) {
            pagination.style.display = filtered.length <= perPage ? 'none' : '';
        }

        // État disabled des boutons prev/next
        if (prevItem) prevItem.classList.toggle('disabled', currentPage <= 1);
        if (nextItem) nextItem.classList.toggle('disabled', currentPage >= tp);
    }

    // Événements navigation
    var prevBtn = prevItem ? prevItem.querySelector('.page-link') : null;
    var nextBtn = nextItem ? nextItem.querySelector('.page-link') : null;

    if (prevBtn) {
        prevBtn.addEventListener('click', function() {
            if (currentPage > 1) {
                currentPage--;
                render();
            }
        });
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', function() {
            if (currentPage < totalPages()) {
                currentPage++;
                render();
            }
        });
    }

    // Premier rendu
    render();

    return {
        /**
         * Filtre les sessions par activité (ou affiche tout si activity est vide/null).
         * Réinitialise la page à 1 et désélectionne la session courante.
         * @param {string} activity
         */
        setFilter: function(activity) {
            filtered    = activity
                ? allSessions.filter(function(s) { return s.activite_titre === activity; })
                : allSessions.slice();
            currentPage = 1;
            selectedId  = null;
            if (onSelect) onSelect(null, null);
            render();
        },

        /**
         * Sélectionne une session par son id, navigue à la bonne page.
         * @param {string|number} id
         * @returns {boolean} true si la session a été trouvée et sélectionnée
         */
        selectById: function(id) {
            var sid = String(id);
            var idx = -1;
            for (var i = 0; i < filtered.length; i++) {
                if (String(filtered[i].id) === sid) { idx = i; break; }
            }
            if (idx === -1) return false;
            currentPage = Math.floor(idx / perPage) + 1;
            selectedId  = sid;
            render();
            var session = null;
            for (var j = 0; j < allSessions.length; j++) {
                if (String(allSessions[j].id) === sid) { session = allSessions[j]; break; }
            }
            if (session && onSelect) {
                var item = list ? list.querySelector('[data-session-id="' + sid + '"]') : null;
                onSelect(session, item);
            }
            return true;
        },

        /**
         * Sélectionne automatiquement la session si une seule est visible dans le filtre actuel.
         * @returns {boolean} true si une session a été sélectionnée
         */
        autoSelectIfSingle: function() {
            if (filtered.length === 1) {
                return this.selectById(filtered[0].id);
            }
            return false;
        },

        /** @returns {string|null} L'id de la session sélectionnée, ou null */
        getSelectedId: function() {
            return selectedId;
        },
    };
}

export { initSessionPagination, paginationControlsHTML };

// Exposition globale pour les scripts inline (ex: map.php) qui ne passent pas par webpack
if (typeof window !== 'undefined') {
    window.goBooking = window.goBooking || {};
    window.goBooking.initSessionPagination  = initSessionPagination;
    window.goBooking.paginationControlsHTML = paginationControlsHTML;
}
